/**
 * GET /v2/portfolio/rebalance-questionnaire.
 *
 * Belongs here: path, axios, query hook, map to the shared Question shape.
 * Does not belong here: answering or submit.
 */
import { useQuery } from '@tanstack/react-query'
import axiosInstance from '../../../../utils/axiosConfig'
import { mobileBackendUrl } from '../../../../env'
import { mapApiQuestionToQuestion } from '../../expert-review/question-utils/mapApiQuestionToQuestion'
import type { Question, QuestionnaireQuestionApi } from '../../expert-review/shared/types/questionnaire.types'

export const REBALANCE_QUESTIONNAIRE_PATH = `${mobileBackendUrl}/v2/portfolio/rebalance-questionnaire`
const REBALANCE_QUESTIONNAIRE_QUERY_KEY = ['rebalance-questionnaire'] as const

export interface RebalanceQuestionnaireResponse {
  questionnaireId?: string
  questions?: QuestionnaireQuestionApi[]
}

/** GET rebalance questions. Saved answers on the payload are ignored — every visit starts fresh. */
export async function fetchRebalanceQuestionnaire(): Promise<RebalanceQuestionnaireResponse> {
  const response = await axiosInstance.get<RebalanceQuestionnaireResponse>(REBALANCE_QUESTIONNAIRE_PATH)
  return response?.data ?? {}
}

export function useRebalanceQuestionnaireQuery() {
  return useQuery({
    queryKey: REBALANCE_QUESTIONNAIRE_QUERY_KEY,
    queryFn: fetchRebalanceQuestionnaire,
    retry: 1
  })
}

/** Same question mapper as strategy. Saved answers are cleared so every visit starts fresh. */
export function mapRebalanceQuestions(questions: QuestionnaireQuestionApi[] | undefined): Question[] {
  const mapped = (questions ?? []).map((question) => {
    const view = mapApiQuestionToQuestion(question)
    return {
      ...view,
      answered: undefined,
      input: {
        ...view.input,
        inputDataString: null,
        inputDataNumber: null,
        selectedOptionIds: null
      }
    }
  })
  return [...mapped].sort((left, right) => (left.order ?? 0) - (right.order ?? 0))
}
